import { getSelectedFiltersMap } from './selectedOptionsUtils';
import { sortFilters } from './sortUtils';

import { Filter, FilterOption } from '~/core/models';
import {
  BuildFilters,
  FilterInput,
  FilterModel,
  Item,
  ListAndFilters,
  Option,
  SelectedFilterOptions,
  TypeOfFilter,
} from '~/core/types/sortAndFilter';

export const buildFilters = (entries: FilterModel[]): BuildFilters => {
  const filters: Record<string, Filter> = {};
  const filterOptions: Record<string, Record<string, number>> = {};

  entries.forEach((entry) => {
    entry.features.forEach((feature) => {
      // Ensure the filter entry exists
      if (!filters[feature.description]) {
        filters[feature.description] = { filterOptions: [], isExpanded: true };
        filterOptions[feature.description] = {};
      }
      // Handle the count of each feature value
      const currentFilterOption = filterOptions[feature.description];

      currentFilterOption[feature.value] =
        (currentFilterOption[feature.value] || 0) + 1;
    });
  });

  return {
    filters,
    filterOptions,
  };
};

export const initializeFiltersOptions = <T extends FilterModel>(
  entries: T[],
  setFilters: (filters: FilterInput) => void,
  prevFilters: FilterInput = {}
): void => {
  const { filters, filterOptions } = buildFilters(entries);

  const selectedOptions = Object.entries(prevFilters).reduce(
    (acc, [key, filter]) => {
      acc[key] = {
        ...filter,
        filterOptions: filter.filterOptions.filter(
          (option: FilterOption) => option.isSelected
        ),
      };

      return acc;
    },
    {} as Record<string, Filter>
  );

  Object.entries(filterOptions).forEach(([key, value]) => {
    const options = Object.entries(value);
    const filterOptionsArray: FilterOption[] = options.map(
      ([name, numberOfItems]) => ({
        name,
        numberOfItems,
        isSelected:
          selectedOptions[key]?.filterOptions.some(
            (option) => option.name === name
          ) ?? false,
      })
    );

    filters[key] = {
      filterOptions: filterOptionsArray,
      isExpanded: selectedOptions[key]?.isExpanded ?? true,
    };
  });

  setFilters(filters);
};

export const filterAllEntries = <T extends FilterModel>(
  selectedFiltersArray: [string, FilterOption[]][],
  browsedElements: T[]
): T[] => {
  let allEntries = browsedElements;

  // iterate over all selected filters
  for (const [key, value] of selectedFiltersArray) {
    // if for a section we have selected filters then filter the browsedElements
    if (value.length > 0) {
      allEntries = allEntries.filter((entry: T) =>
        entry.features.find(
          (feature) =>
            feature.description === key &&
            value.find((val: FilterOption) => val.name === feature.value)
        )
      );
    }
  }

  return allEntries;
};

export const updateFiltersOptions = <T extends FilterModel>(
  filters: FilterInput,
  browsedEntries: T[],
  callback?: (updatedFilters: FilterInput) => void
): void => {
  const clonedFilters = structuredClone(filters);

  // This will hold the selected filters
  const selectedFilters = getSelectedFiltersMap(clonedFilters);

  for (const filterKey in clonedFilters) {
    if (!Object.hasOwnProperty.call(clonedFilters, filterKey)) {
      continue;
    }
    const filterValue = clonedFilters[filterKey];

    // Remove the current filter key from the selected filters for independent calculation
    const currentFilterOptions = selectedFilters[filterKey];

    delete selectedFilters[filterKey];

    const allEntries = filterAllEntries(
      Object.entries(selectedFilters),
      browsedEntries
    );
    const filters = filterValue.filterOptions;

    filters.forEach((filter) => {
      // Count the entries that match the current filter option
      const counter = allEntries.filter((entry: T) =>
        entry.features.find(
          (feature) =>
            feature.description === filterKey && feature.value === filter.name
        )
      ).length;

      // If there are no entries that match the current filter then set the isSelected flag to false
      if (counter === 0) {
        filter.isSelected = false;
      }

      // Update the number of entries
      filter.numberOfItems = counter;
    });

    // Add back the deleted filters into the record for complete state restoration
    if (currentFilterOptions) {
      selectedFilters[filterKey] = currentFilterOptions;
    }
  }

  if (callback) {
    callback(clonedFilters);
  }
};

export const updateListAndFilters = <T extends object = object>(
  sourceList: Item<T>[],
  selectedOptionsProp: SelectedFilterOptions
): ListAndFilters => {
  const selectedOptions = Object.entries(selectedOptionsProp);
  const { ids, filters } = sourceList.reduce<ListAndFilters>(
    (result, { features, productId }) => {
      const isItemMatchingFilters = selectedOptions.every(
        ([selectedCategory, selectedOption]) =>
          selectedOption.every((option) =>
            features.some(
              (feature) =>
                feature.description === selectedCategory &&
                feature.value === option
            )
          )
      );

      const updatedFilters = features.reduce<TypeOfFilter[]>(
        (
          filters,
          { description: categoryTitle, value: optionLabel, position }
        ) => {
          const maybeCategoryIndex = filters.findIndex(
            ([filterCat]) => filterCat === categoryTitle
          );
          const [categoryIndex, options]: [number, Option[]] =
            maybeCategoryIndex > -1
              ? [maybeCategoryIndex, filters[maybeCategoryIndex][1]]
              : [filters.length, []];
          const maybeOptionIndex = options.findIndex(
            ({ label }) => label === optionLabel
          );
          const [optionIndex, option]: [number, Option] =
            maybeOptionIndex > -1
              ? [maybeOptionIndex, options[maybeOptionIndex]]
              : [options.length, { label: optionLabel, qty: 0 }];

          option.qty = option.qty + (isItemMatchingFilters ? 1 : 0);
          options[optionIndex] = option;
          filters[categoryIndex] = [categoryTitle, options, position || 0];

          return filters;
        },
        [...result.filters]
      );

      return {
        filters: updatedFilters,
        ids: isItemMatchingFilters ? [...result.ids, productId] : result.ids,
      };
    },
    { filters: [], ids: [] }
  );

  return { ids, filters: sortFilters(filters) };
};
